import { useState, useCallback, useEffect, useRef } from 'react';
import { 
  login as authLogin, 
  logout as authLogout, 
  getUser, 
  isAuthenticated as checkTokenValidity, 
  getToken,
  updateProfile as authUpdateProfile
} from '../services/auth';

export interface User {
  email?: string;
  name?: string;
  picture?: string;
  nickname?: string;
  given_name?: string;
  family_name?: string;
  sub?: string;
  user_metadata?: {
    [key: string]: any;
  };
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initializingRef = useRef(true);

  // Function to check and update auth state
  const checkAuthState = useCallback(async () => {
    console.log('Checking auth state...');
    const token = await getToken();
    if (!token) {
      console.log('No token found');
      return false;
    }

    const isValid = await checkTokenValidity();
    console.log('Token valid:', isValid);
    if (!isValid) {
      console.log('Token invalid, clearing session...');
      await authLogout();
      setUser(null);
      return false;
    }

    const userData = await getUser();
    console.log('User data:', userData);
    if (userData) {
      setUser(userData);
      return true;
    }

    return false;
  }, [setUser]); // Add setUser to dependencies to stabilize the function reference

  const handleLogin = useCallback(async (username: string, password: string) => {
    console.log('Login attempt for:', username);
    setLoading(true);
    setError(null);
    try {
      const response = await authLogin(username, password);
      console.log('Login response:', response);
      
      if (response.success) {
        // Verify the auth state after login
        let retries = 5;
        let isValid = false;
        
        while (retries > 0 && !isValid) {
          console.log(`Verifying auth state (${retries} retries left)...`);
          isValid = await checkAuthState();
          if (isValid) {
            console.log('Auth state verified successfully');
            console.log('Current user state:', user);
            // Don't set loading to false here, let the effect handle it
            return true;
          }
          console.log('Auth state not ready, waiting...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          retries--;
        }
        
        // If we get here, auth state verification failed
        console.error('Failed to verify auth state after multiple attempts');
        setError('Failed to verify authentication');
        setLoading(false);
        return false;
      } else {
        console.error('Login failed:', response.data);
        setError(response.data.error_description || 'Login failed');
        setLoading(false);
        return false;
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err instanceof Error ? err.message : 'Login failed');
      setLoading(false);
      return false;
    }
  }, [checkAuthState, user]);

  // Effect to update loading state when user changes
  useEffect(() => {
    // Clear loading state immediately when we have user data
    if (user) {
      console.log('User state updated:', user);
      setLoading(false);
    }
  }, [user]);

  // Effect to update loading state when user changes
  useEffect(() => {
    // Clear loading state immediately when we have user data
    if (user) {
      setLoading(false);
    }
  }, [user]);

  const handleLogout = useCallback(async () => {
    console.log('Logging out...');
    setLoading(true);
    try {
      await authLogout();
      setUser(null);
      setError(null);
      console.log('Logout complete');
    } catch (err) {
      console.error('Logout error:', err);
      setError(err instanceof Error ? err.message : 'Logout failed');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize auth state once on mount
  useEffect(() => {
    const initAuth = async () => {
      if (!initializingRef.current) return;
      
      console.log('Initializing auth state...');
      try {
        await checkAuthState();
      } catch (err) {
        console.error('Failed to initialize auth:', err);
      } finally {
        setLoading(false);
        initializingRef.current = false;
      }
    };

    initAuth();
  }, [checkAuthState]); // Add checkAuthState as dependency since it's used in the effect

  const isAuthenticated = !!user;
  
  return {
    user,
    loading,
    error,
    login: handleLogin,
    logout: handleLogout,
    isAuthenticated,
    updateProfile: authUpdateProfile
  };
};
